import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo, useEffectEvent, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useSocketEvent } from '../hooks/useSocket';
import { SocketEvents } from '../constants/socket-events';
import { config } from '../config';
import { AllowedPage, Tenant, TenantBot } from '../types/dashboard';
import { tr } from '../i18n/tr';


export type TenantAction =
  | { type: 'SET_TENANT'; payload: { tenantId: string, pageId?: string } }
  | { type: 'SET_PAGE'; payload: { pageId: string } }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'REFRESH_PAGES'; payload: {  } };

interface ITenantContext {
  // Current state
  selectedTenantId: string | null;
  selectedPageId: string | null;

  // Data
  tenants: Tenant[];
  /** Connected pages only - for live chat (PageSelector, PageDashboard) */
  tenantPages: Record<string, AllowedPage[]>;
  /** Bots per tenant - for flow builder (with or without connected pages) */
  tenantBots: Record<string, TenantBot[]>;
  loading: boolean;
  error: string | null;

  // Actions
  dispatch: (action: TenantAction) => void;
  /** Manual refetch — bypasses dispatch boilerplate. Used by error-recovery UI and the OPERATOR_ACCESS_CHANGED socket listener. */
  refetch: () => void;
  /**
   * A tenant a global admin opened without being a member (resolved by useResolvedTenant).
   * Its pages come from allowed-pages once `X-Tenant-Id` names it; it is merged into `tenants`.
   */
  contextTenant: Tenant | null;
  setContextTenant: (tenant: Tenant | null) => void;
}

export const TenantContext = createContext<ITenantContext | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}


export function TenantProvider({ children }: TenantProviderProps) {
  const { apiFetch, refreshUser } = useAuth();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantPages, setTenantPages] = useState<Record<string, AllowedPage[]>>({});
  const [tenantBots, setTenantBots] = useState<Record<string, TenantBot[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [last, setLast] = useState<number>(Date.now()); // for refreshing pages
  const [contextTenant, setContextTenant] = useState<Tenant | null>(null);

  // Last-write-wins: only the newest allowed-pages request may update state.
  const requestSeq = useRef(0);

  const fetchPages = useEffectEvent(async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      setError(null);

      // apiFetch handles Bearer + auto-refresh-and-retry on 401.
      const resp = await apiFetch(
        `${config.apiServer}/api/system/operator/allowed-pages`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // Always name a global admin's context tenant, whatever tenant is active right now,
            // so a refetch from elsewhere (access-changed, manual) never drops its pages.
            ...(contextTenant ? { 'X-Tenant-Id': contextTenant.id } : {}),
          },
        },
      );
      if (seq !== requestSeq.current) return;

      if (!resp.ok) {
        throw new Error(`Failed to fetch pages: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      if (seq !== requestSeq.current) return;
      const nextTenants: Tenant[] = data?.tenants || [];
      const nextTenantPages: Record<string, AllowedPage[]> = data?.tenantPages || {};
      const nextTenantBots: Record<string, TenantBot[]> = data?.tenantBots || {};

      setTenants(nextTenants);
      setTenantPages(nextTenantPages);
      setTenantBots(nextTenantBots);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setError(err?.message || tr('inbox.tenantsFailed'));
      setTenants([]);
      setTenantPages({});
      setTenantBots({});
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  });

  // Load pages on mount, when `last` is bumped (manual refetch / socket event), or when a global
  // admin's context tenant changes (allowed-pages then includes that tenant's pages).
  useEffect(() => {
    fetchPages();
  }, [last, contextTenant?.id]);

  // The gateway lists a context tenant as a bare `{ id }`; show it with its name/slug.
  const mergedTenants = useMemo(() => {
    if (!contextTenant) return tenants;
    return [...tenants.filter((t) => t.id !== contextTenant.id), contextTenant];
  }, [tenants, contextTenant]);

  const refetch = useCallback(() => {
    setLast(Date.now());
  }, []);

  const dispatch = useCallback((action: TenantAction) => {
    switch (action.type) {
      case 'SET_TENANT':
        setSelectedTenantId(action.payload.tenantId);
        if (action.payload?.pageId) {
          setSelectedPageId(action.payload.pageId);
        }
        break;
      case 'SET_PAGE':
        setSelectedPageId(action.payload.pageId);
        break;
      case 'REFRESH_PAGES':
        setLast(Date.now());
        break;
      default:
        break;
    }
  }, []);

  // PR-C: when the gateway tells us our access has changed (tenant membership added/removed,
  // page connected/disconnected), refetch tenants+pages AND re-pull userinfo so the JWT-derived
  // tenants[] in AuthContext stays in sync. Gateway side of this event is tracked in
  // messaging-gateway/ROADMAP.md as Initiative: live-access-refresh.
  const handleAccessChanged = useCallback(() => {
    refetch();
    void refreshUser();
  }, [refetch, refreshUser]);
  useSocketEvent(SocketEvents.OPERATOR_ACCESS_CHANGED, handleAccessChanged);

  const value: ITenantContext = useMemo(
    () => ({
      selectedTenantId,
      selectedPageId,
      tenants: mergedTenants,
      tenantPages,
      tenantBots,
      loading,
      error,
      dispatch,
      refetch,
      contextTenant,
      setContextTenant,
    }),
    [selectedTenantId, selectedPageId, mergedTenants, tenantPages, tenantBots, loading, error, dispatch, refetch, contextTenant],
  );

  // Don't render loading here - let App.tsx handle it
  // This allows App.tsx to coordinate both auth and tenantPage loading states
  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}

